import { useState, useEffect } from "react";

/**
 * A custom React hook to detect when an element is visible in the viewport.
 * Triggers only once.
 *
 * @param {React.RefObject} ref - A ref attached to the element to observe.
 * @param {object} options - Configuration options for the IntersectionObserver.
 * @returns {boolean} - True if the element is intersecting, otherwise false.
 */
const useOnScreen = (ref, options = { threshold: 0.5 }) => {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      // If the element is intersecting (visible)
      if (entry.isIntersecting) {
        setIntersecting(true);
        // Stop observing the element after it has become visible
        observer.unobserve(entry.target);
      }
    }, options);

    const currentElement = ref.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [ref, options]); // Only re-run the effect if ref or options change

  return isIntersecting;
};

export default useOnScreen;
